import React, { useMemo } from 'react';

import { Asset2ReviewStatus } from '../../../../../../apis/graphql';
import CreativeReviewBadge from '../../../../CreativeReviewBadge';
import { useCalculatedCreativeDataByAdCountries } from '../../../hooks/useCalculatedCreativeDataByAdCountries';
import type { CreativesBuilderValues } from '../../../types';
import { reviewAlertPropsMapV2 } from '../../../utils';
import RejectionMessage from '../RejectionMessage';
import type { CreativeFooterAsset, LineItemData } from '../types';
import { getReviewAlertData } from './utils';

export type CreativeReviewAlertProps = {
  assets: CreativeFooterAsset[];
  creative: CreativesBuilderValues;
  isLoading?: boolean;
  lineItemData: LineItemData;
};

const CreativeReviewAlertAsset2 = ({
  assets,
  creative,
  lineItemData,
  isLoading,
}: CreativeReviewAlertProps): React.JSX.Element => {
  const {
    derivedCreativeReviewStatus: creativeReviewStatus,
    rejectionReasons,
    loading: asset2Loading,
  } = useCalculatedCreativeDataByAdCountries(creative, lineItemData, isLoading);

  const reviewAlertProps = creativeReviewStatus ? reviewAlertPropsMapV2[creativeReviewStatus] : null;

  const reviewAlertMessage = useMemo(() => {
    if (!creativeReviewStatus) return null;
    if (creativeReviewStatus !== Asset2ReviewStatus.Declined) return reviewAlertProps?.bodyText;
    if (!rejectionReasons) return null;
    const { rejectedAssets, footerCreative } = getReviewAlertData(assets, creative, rejectionReasons);

    return (
      <RejectionMessage
        bodyText={reviewAlertProps?.bodyText}
        rejectedAssets={rejectedAssets}
        creative={footerCreative}
      />
    );
  }, [assets, creative, creativeReviewStatus, rejectionReasons, reviewAlertProps?.bodyText]);

  return (
    <>
      {reviewAlertProps && reviewAlertMessage ? (
        <CreativeReviewBadge
          alertProps={{ ...reviewAlertProps, bodyText: reviewAlertMessage }}
          creativeReview={creativeReviewStatus}
          loading={asset2Loading || isLoading}
        />
      ) : null}
    </>
  );
};

export default CreativeReviewAlertAsset2;
